import React, { useState } from 'react';
import { Autocomplete } from '@react-google-maps/api';
import { parseGoogleAddress } from '@/utils/maps.ts';
import TextFieldWithCheck from '@/components/form/TextFieldWithCheck.tsx';
import { Typography } from '@mui/material';
import { useGoogleMaps } from '@/context/GoogleMapsProvider.tsx';

interface ErrorFields {
  address?: boolean;
  city?: boolean;
  postalCode?: boolean;
  province?: boolean;
}

interface HelperTexts {
  address?: string;
  city?: string;
  postalCode?: string;
  province?: string;
}

interface AddressFieldsProps {
  formData: {
    address: string;
    city: string;
    postalCode: string;
    province: string;
    country?: string;
  };
  errorFields?: ErrorFields;
  helperTexts?: HelperTexts;
  onChange: (params: {
    address: string;
    city: string;
    postalCode: string;
    province: string;
    country?: string;
  }) => void;
  setErrorFields?: React.Dispatch<React.SetStateAction<ErrorFields>>;
  postalCodeRequired?: boolean;
  onFieldBlur?: (field: keyof ErrorFields) => void;
}

const AddressFields: React.FC<AddressFieldsProps> = ({
  formData,
  errorFields,
  helperTexts,
  onChange,
  setErrorFields,
  postalCodeRequired = true,
  onFieldBlur,
}) => {
  // eslint-disable-next-line no-undef
  const [autocomplete, setAutocomplete] = useState<google.maps.places.Autocomplete | null>(null);
  const { isLoaded, loadError } = useGoogleMaps();
  const isSpain = (formData.country ?? 'ES') === 'ES';
  const provinceLabel = isSpain ? 'Provincia(*)' : 'Región / provincia(*)';

  if (loadError) {
    return <Typography color="error">Error cargando Google Maps API</Typography>;
  }

  if (!isLoaded) {
    return <Typography>Cargando Google Maps API...</Typography>;
  }

  // eslint-disable-next-line no-undef
  const onLoad = (autoC: google.maps.places.Autocomplete) => {
    setAutocomplete(autoC);
  };

  const onPlaceChanged = () => {
    if (!autocomplete) return;
    const place = autocomplete.getPlace();
    const details = parseGoogleAddress(place.address_components || []);
    const newAddress = `${details.route || ''}${
      details.streetNumber ? ', ' + details.streetNumber : ''
    }`;

    onChange({
      address: newAddress,
      city: details.city ?? '',
      province: details.province ?? '',
      postalCode: details.postalCode ?? '',
      country: details.countryCode || formData.country,
    });

    if (!setErrorFields) return;
    setErrorFields((prev) => ({
      ...prev,
      address: !place.formatted_address?.trim(),
      city: !details.city?.trim(),
      province: !details.province?.trim(),
      postalCode: postalCodeRequired && !details.postalCode?.trim(),
    }));
  };

  const handleBlur = (field?: keyof ErrorFields) => {
    if (field) {
      onFieldBlur?.(field);
    }

    if (!setErrorFields) return;

    const nextState: ErrorFields = {
      address: !formData.address.trim(),
      city: !formData.city.trim(),
      province: !formData.province.trim(),
      postalCode: postalCodeRequired && !formData.postalCode.trim(),
    };

    setErrorFields((prev) => ({
      ...prev,
      ...(field ? { [field]: nextState[field] } : nextState),
    }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    onChange({ ...formData, [name]: value });
  };

  return (
    <>
      <Autocomplete onLoad={onLoad} onPlaceChanged={onPlaceChanged}>
        <TextFieldWithCheck
          id="address"
          name="address"
          label="Dirección(*)"
          variant="outlined"
          error={!!errorFields?.address}
          helperText={helperTexts?.address}
          value={formData.address}
          onChange={handleChange}
          onBlur={() => handleBlur('address')}
          fullWidth
        />
      </Autocomplete>
      <TextFieldWithCheck
        id="postalCode"
        name="postalCode"
        label={postalCodeRequired ? 'Código postal(*)' : 'Código postal'}
        variant="outlined"
        error={!!errorFields?.postalCode}
        helperText={helperTexts?.postalCode}
        value={formData.postalCode}
        onChange={handleChange}
        onBlur={() => handleBlur('postalCode')}
      />
      <TextFieldWithCheck
        id="city"
        name="city"
        label="Población(*)"
        variant="outlined"
        error={!!errorFields?.city}
        helperText={helperTexts?.city}
        value={formData.city}
        onChange={handleChange}
        onBlur={() => handleBlur('city')}
      />
      <TextFieldWithCheck
        id="province"
        name="province"
        label={provinceLabel}
        variant="outlined"
        error={!!errorFields?.province}
        helperText={helperTexts?.province}
        value={formData.province}
        onChange={handleChange}
        onBlur={() => handleBlur('province')}
      />
    </>
  );
};

export default AddressFields;
